/**
 * Reset Password Page
 * Handles password reset flow for returning users with Dragonfly context preservation
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, ArrowRight, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { useLocation } from 'wouter';
import { dragonflyUserService } from '../services/DragonflyUserService';

export const ResetPasswordPage: React.FC = () => {
  const [, setLocation] = useLocation();
  const { updatePassword, user, session, sessionReady } = useSupabaseAuth();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragonflyInfo, setDragonflyInfo] = useState<any>(null);
  const [sessionValid, setSessionValid] = useState(true);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    // CRITICAL: Clear any stale auth state first to prevent showing "Welcome back" page
    const clearStaleAuth = () => {
      console.log('[ResetPassword] Clearing stale auth state...');

      // Clear localStorage items that might cause "Welcome back" to appear
      const emailKey = Object.keys(localStorage).find(key => key.includes('user_email'));
      if (emailKey) {
        console.log('[ResetPassword] Removing cached email from localStorage');
        localStorage.removeItem(emailKey);
      }

      // Clear any auth mode hints
      localStorage.removeItem('auth_mode');
      localStorage.removeItem('auth_step');
    };

    clearStaleAuth();

    // Check if we have a valid recovery session
    const checkRecoverySession = async () => {
      // Wait for session to be ready before checking
      if (!sessionReady) {
        console.log('[ResetPassword] Waiting for session to be ready...');
        return;
      }

      console.log('[ResetPassword] Session ready, checking validity...', { hasSession: !!session, hasUser: !!user });

      if (!session || !user) {
        console.error('[ResetPassword] No valid recovery session found');
        setSessionValid(false);
        setError('Invalid or expired reset link. Please request a new password reset.');
      } else {
        console.log('[ResetPassword] Valid recovery session found for:', user.email);
      }

      setCheckingSession(false);
    };

    checkRecoverySession();

    // Check if user is a returning Dragonfly user
    const checkDragonflyStatus = async () => {
      if (user?.email) {
        const info = await dragonflyUserService.checkDragonflyUser(user.email);
        if (info.isDragonflyUser) {
          setDragonflyInfo(info);
        }
      }
    };
    
    checkDragonflyStatus();
  }, [user, session, sessionReady]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await updatePassword(newPassword);
      setSuccess(true);
      
      // If Dragonfly user, sync their data after password reset
      if (dragonflyInfo?.isDragonflyUser && dragonflyInfo.sourcePlatform) {
        await dragonflyUserService.syncDragonflyUserData(
          user?.email || '',
          dragonflyInfo.sourcePlatform
        );
      }

      // Redirect after success
      setTimeout(async () => {
        if (dragonflyInfo?.isDragonflyUser) {
          // Handle Dragonfly user journey routing
          const journeyUrl = await dragonflyUserService.handleDragonflyUserJourney(dragonflyInfo);
          if (journeyUrl.startsWith('http')) {
            window.location.href = journeyUrl;
          } else {
            setLocation(journeyUrl);
          }
        } else {
          // Regular user - go to portal
          setLocation('/portal');
        }
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-black flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            Red Helicopter
          </h1>
          <p className="text-purple-300">Create Your New Password</p>
        </div>

        {/* Reset Card */}
        <motion.div
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-purple-500/20"
          layout
        >
          {checkingSession ? (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Verifying Reset Link...</h2>
              <p className="text-purple-300">Please wait while we validate your password reset request.</p>
            </div>
          ) : success ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center"
            >
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-white mb-2">Password Reset Successfully!</h2>
              <p className="text-purple-300 mb-2">
                Your password has been updated for:
              </p>
              <p className="text-white font-medium mb-4">
                {user?.email}
              </p>
              <p className="text-purple-300 mb-4">
                {dragonflyInfo?.welcomeMessage || 'Redirecting you to the app...'}
              </p>
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                <span className="text-purple-400 text-sm">Please wait...</span>
              </div>
            </motion.div>
          ) : (
            <form onSubmit={handlePasswordReset}>
              <h2 className="text-2xl font-semibold text-white mb-2">Set Your New Password</h2>
              
              {/* Show session status and user email */}
              {sessionValid ? (
                <div className="mb-6">
                  <p className="text-purple-300 mb-2">
                    Setting new password for:
                  </p>
                  <p className="text-white font-medium bg-purple-900/30 border border-purple-500/30 rounded-lg px-4 py-2">
                    {user?.email || 'Loading...'}
                  </p>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-red-900/30 border border-red-500/30 rounded-lg">
                  <p className="text-red-200 text-sm">
                    Your password reset link has expired or is invalid.
                  </p>
                  <button
                    type="button"
                    onClick={() => setLocation('/login')}
                    className="mt-2 text-red-400 hover:text-red-300 underline text-sm"
                  >
                    Request a new password reset
                  </button>
                </div>
              )}
              
              {/* Show welcome message for returning Dragonfly users */}
              {dragonflyInfo?.welcomeMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-lg"
                >
                  <p className="text-white text-sm leading-relaxed">{dragonflyInfo.welcomeMessage}</p>
                </motion.div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-purple-200 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400 w-5 h-5" />
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-white/5 border border-purple-500/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400 focus:bg-white/10 transition"
                      placeholder="Enter new password (min 6 characters)"
                      autoFocus
                      required
                      minLength={6}
                      disabled={!sessionValid}
                    />
                  </div>
                  <p className="text-purple-400 text-xs mt-1">
                    Password must be at least 6 characters long
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-purple-200 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400 w-5 h-5" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-white/5 border border-purple-500/30 rounded-lg text-white placeholder-purple-300/50 focus:outline-none focus:border-purple-400 focus:bg-white/10 transition"
                      placeholder="Confirm new password"
                      required
                      minLength={6}
                      disabled={!sessionValid}
                    />
                  </div>
                  {newPassword && confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-red-400 text-xs mt-1">
                      Passwords do not match
                    </p>
                  )}
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm flex items-start gap-2"
                  >
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </motion.div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || !newPassword || !confirmPassword || !sessionValid}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Reset Password
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setLocation('/login')}
                  className="text-sm text-purple-400 hover:text-purple-300"
                >
                  Back to Login
                </button>
              </div>
            </form>
          )}
        </motion.div>

        {/* Dragonfly User Info */}
        {dragonflyInfo?.isDragonflyUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-4 text-center text-purple-300 text-sm"
          >
            {dragonflyInfo.sourcePlatform === 'aleris' && (
              <p>Aleris Life member • Biological systems await</p>
            )}
            {dragonflyInfo.hasCompletedJourney && (
              <p>Journey Complete • AI Portal ready</p>
            )}
            {dragonflyInfo.journeyProgress > 0 && !dragonflyInfo.hasCompletedJourney && (
              <p>Journey {Math.round(dragonflyInfo.journeyProgress * 100)}% complete</p>
            )}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};